import { defaultEditorDefns } from "__dummy__/TestEditor";
import { useCallback } from "react";
import styles from "../aic.module.css";
import { EditorDefns } from "../types/interface";
import { AioIconButton } from "./aioIconButton";
import { AioLabel } from "./aioLabel";
import { AioReplacementDisplay } from "./aioReplacementDisplay";
import { AioExternalReplacements, AioReplacement } from "./interface";
import { newReplacement } from "./newReplacement";

/**
 * Properties for AioReplacements.
 */
export interface AioReplacementListProps<T> {
  id: string;
  label?: string;
  replacements: AioReplacement<T>[];
  setReplacements?: (ret: AioReplacement<T>[]) => void;
  dontAskSpace?: boolean;
  dontAskTrail?: boolean;
  externalLists?: AioExternalReplacements<T>[];
  editorProps?: EditorDefns<T>;
  level: number;
}

/**
 * Option item for replacements.
 *
 * @param props - Destructured parameter object
 * @param props.id - Id for the control
 * @param props.label - Optional label for the list
 * @param props.replacements - The replacements to display
 * @param props.setReplacements - The setReplacements function
 * @param props.dontAskSpace - The dontAskSpace boolean
 * @param props.dontAskTrail - The dontAskTrail boolean
 * @param props.externalLists - Any available externalLists
 * @param props.editorProps - Used to configure the text editor
 * @param props.level - The current nesting level
 */
export const AioReplacementList = <T,>({
  id,
  label,
  replacements,
  setReplacements,
  dontAskSpace,
  dontAskTrail,
  externalLists,
  editorProps = defaultEditorDefns as EditorDefns<T>,
  level,
}: AioReplacementListProps<T>): React.ReactElement => {
  const updateReplacement = useCallback(
    (ret: AioReplacement<T>, i: number) => {
      /* istanbul ignore else */
      if (setReplacements) {
        const newValue = [...replacements];
        newValue[i] = ret;
        setReplacements(newValue);
      }
    },
    [replacements, setReplacements],
  );

  const addReplacement = useCallback(
    (i: number) => {
      /* istanbul ignore else */
      if (setReplacements) {
        const newReplacements = [...replacements];
        newReplacements.splice(i, 0, newReplacement(editorProps.blankT));
        setReplacements(newReplacements);
      }
    },
    [editorProps.blankT, replacements, setReplacements],
  );

  const removeReplacement = useCallback(
    (i: number) => {
      /* istanbul ignore else */
      if (setReplacements) {
        const newReplacements = [...replacements];
        newReplacements.splice(i, 1);
        setReplacements(newReplacements);
      }
    },
    [replacements, setReplacements],
  );

  return (
    <>
      <AioLabel
        id={`${id}-label`}
        label={label}
      />
      <div>
        {level < 5 && (
          <>
            {!label && (
              <>
                <span>then...</span>{" "}
              </>
            )}
            {setReplacements && (
              <div
                id={`${id}-addreplacement`}
                title={"Add replacement"}
                className={[styles.aioxButton, styles.aioxAddDown].join(" ")}
                onClick={() => addReplacement(0)}
              />
            )}
          </>
        )}
        {replacements.map((repl, i) => {
          return (
            <div key={`${i}-${repl.airid}`}>
              {i > 0 && <div> and...</div>}
              <AioReplacementDisplay
                id={`${id}-replacementdisplay-${i}`}
                airid={repl.airid}
                oldText={repl.oldText}
                newTexts={repl.newTexts}
                includeTrailing={repl.includeTrailing}
                externalName={repl.externalName}
                setReplacement={
                  setReplacements
                    ? (ret: AioReplacement<T>) => updateReplacement(ret, i)
                    : undefined
                }
                dontAskSpace={dontAskSpace}
                dontAskTrail={dontAskTrail}
                externalLists={externalLists}
                editorProps={editorProps}
                level={level}
              />
              {setReplacements && (
                <div
                  className={[
                    styles.aioxButtonHolder,
                    styles.flexRowCenter,
                    styles.marginBottom2,
                  ].join(" ")}
                >
                  {replacements && replacements.length >= 1 && (
                    <AioIconButton
                      id={`${id}-remove`}
                      iconName={"aioxRemoveUp"}
                      onClick={() => removeReplacement(i)}
                      tipText={"Remove old text"}
                    />
                  )}
                  <AioIconButton
                    id={`${id}-add`}
                    iconName={"aioxAddDown"}
                    onClick={() => addReplacement(i + 1)}
                    tipText={"Add old text"}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
};

AioReplacementList.displayName = "AioReplacementList";
